import { useEffect, useState } from "react";

// Session for email/password (non-SSO) sign-ins. MSAL owns its own session
// state internally (accounts, tokens) - this is a small parallel store for
// the local-JWT path, since those users have no MSAL account at all.
const TOKEN_KEY = "localAuthToken";
const DISPLAY_NAME_KEY = "localAuthDisplayName";
const CHANGE_EVENT = "local-auth-changed";

export interface LocalAuthSession {
  token: string;
  displayName: string;
  tenantName: string;
}

export function getLocalAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setLocalAuthSession(session: LocalAuthSession): void {
  localStorage.setItem(TOKEN_KEY, session.token);
  localStorage.setItem(DISPLAY_NAME_KEY, session.displayName);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function clearLocalAuthSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(DISPLAY_NAME_KEY);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Re-renders on login/logout via a window event, since this lives outside React state. */
export function useLocalAuth() {
  const [token, setToken] = useState(getLocalAuthToken());

  useEffect(() => {
    const handleChange = () => setToken(getLocalAuthToken());
    window.addEventListener(CHANGE_EVENT, handleChange);
    return () => window.removeEventListener(CHANGE_EVENT, handleChange);
  }, []);

  return {
    isAuthenticated: !!token,
    displayName: localStorage.getItem(DISPLAY_NAME_KEY)
  };
}
